import { getHostRuntime } from './host-runtime'
import type { EntityClassName, EntityRegistry, WriteConfig } from './types'

/**
 * The per-host entity registry, injected through HostRuntime at bootstrap. Generic
 * core tools read the registry through this accessor rather than importing the
 * concrete NeoCRM value, so the core carries no entity vocabulary and stays
 * byte-identical across hosts. The connector supplies the value in setHostRuntime().
 */
export function getEntityRegistry(): EntityRegistry {
  return getHostRuntime().registry
}

/**
 * Sentinel `forceAtCreate` value resolved to `ctx.currentUserId` at create time.
 * Lets a registry entry force an owner field (e.g. List `responsible`) to the acting
 * user without hardcoding a per-session id in the static registry.
 */
export const FORCE_CURRENT_USER = '@currentUser'

// ─── Write-ops helper functions ───────────────────────────────────────────────

export function isCreatable(entityType: EntityClassName): boolean {
  return getEntityRegistry()[entityType]?.write?.create === true
}

export function isUpdatable(entityType: EntityClassName): boolean {
  return getEntityRegistry()[entityType]?.write?.update === true
}

export function getWriteConfig(
  entityType: EntityClassName,
): WriteConfig | undefined {
  return getEntityRegistry()[entityType]?.write
}

export function getForceAtCreate(
  entityType: EntityClassName,
): Record<string, string> | undefined {
  return getEntityRegistry()[entityType]?.forceAtCreate
}

/**
 * True when an entity is available on this instance: it has no gating flag, or
 * the flag is present in the instance's enabled set. Single source of truth for
 * "does this instance have this entity", mirroring the detail-page tab gate
 * (filterTabsByFeatureFlags) and the sidebar (isPageEnabled) in
 * src/utils/featureFlagConfig.ts. Used to hide a parent record's relations whose
 * target entity is disabled, so the bot never offers Deal pipeline / Tickets
 * sections on an instance that lacks Opportunity / Quote / Order / Ticket.
 */
export function isEntityEnabled(
  entityType: EntityClassName,
  featureFlags: Set<string>,
): boolean {
  const flag = getEntityRegistry()[entityType]?.requiredFlag
  return !flag || featureFlags.has(flag)
}
